<template>
    <div class="contact-page">
        <header class="contact-hero">
            <div class="contact-hero-content">
                <div class="hero-inner">
                    <h1 class="contact-title">Contact</h1>
                    <p class="contact-subtitle">Une question ou une suggestion ? Remplissez le formulaire ci-dessous.</p>
                </div>
            </div>
        </header>

        <main class="contact-container">
            <section class="contact-content">
                <form class="contact-form" @submit.prevent="submitForm" novalidate>
                    <div class="form-group">
                        <label class="form-label" for="contact-name">Nom complet</label>
                        <input
                            id="contact-name"
                            v-model="form.name"
                            type="text"
                            class="form-input"
                            :class="{ 'form-input--error': errors.name }"
                            placeholder="Votre nom complet"
                            autocomplete="name"
                        />
                        <span v-if="errors.name" class="form-error">{{ errors.name }}</span>
                    </div>

                    <div class="form-group">
                        <label class="form-label" for="contact-email">Adresse mail</label>
                        <input
                            id="contact-email"
                            v-model="form.email"
                            type="email"
                            class="form-input"
                            :class="{ 'form-input--error': errors.email }"
                            placeholder="votre@email.com"
                            autocomplete="email"
                        />
                        <span v-if="errors.email" class="form-error">{{ errors.email }}</span>
                    </div>

                    <div class="form-group">
                        <label class="form-label" for="contact-subject">Sujet</label>
                        <input
                            id="contact-subject"
                            v-model="form.subject"
                            type="text"
                            class="form-input"
                            :class="{ 'form-input--error': errors.subject }"
                            placeholder="Sujet de votre message"
                        />
                        <span v-if="errors.subject" class="form-error">{{ errors.subject }}</span>
                    </div>

                    <div class="form-group">
                        <label class="form-label" for="contact-message">Message</label>
                        <textarea
                            id="contact-message"
                            v-model="form.message"
                            class="form-textarea"
                            :class="{ 'form-input--error': errors.message }"
                            placeholder="Votre message..."
                            rows="6"
                        ></textarea>
                        <span v-if="errors.message" class="form-error">{{ errors.message }}</span>
                    </div>

                    <div v-if="errors.general" class="form-error-general">
                        {{ errors.general }}
                    </div>

                    <div v-if="showRecaptchaNotice" class="recaptcha-notice">
                        Ce site est protégé par reCAPTCHA et les
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">Règles de confidentialité</a> et
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener">Conditions d'utilisation</a>
                        de Google s'appliquent.
                    </div>

                    <button type="submit" class="form-submit" :disabled="isSubmitting">
                        <span v-if="isSubmitting">Envoi en cours...</span>
                        <span v-else>Envoyer le message</span>
                    </button>
                </form>
            </section>
        </main>

        <footer class="footer-section">
            <div class="footer-container">
                <div class="footer-left">
                    <div class="footer-logo-section">
                        <img src="https://i.imgur.com/z198fac.png" alt="MyEcoCar Logo" class="footer-logo" />
                        <span class="footer-brand-name">MyEcoCar</span>
                    </div>
                    <div class="footer-social-links">
                        <a href="#" class="social-link" title="Facebook">
                            <i class="fa fa-facebook-f"></i>
                        </a>
                        <a href="#" class="social-link" title="Twitter">
                            <i class="fa fa-twitter"></i>
                        </a>
                        <a href="#" class="social-link" title="Instagram">
                            <i class="fa fa-instagram"></i>
                        </a>
                        <a href="#" class="social-link" title="LinkedIn">
                            <i class="fa fa-linkedin"></i>
                        </a>
                    </div>
                </div>

                <div class="footer-center">
                    <div class="footer-column">
                        <h4 class="footer-column-title">Infos</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'cgu' }">CGU</router-link></li>
                            <li><router-link :to="{ name: 'faq' }">FAQ</router-link></li>
                            <li><router-link :to="{ name: 'contact' }">Contact</router-link></li>
                        </ul>
                    </div>

                    <div class="footer-column">
                        <h4 class="footer-column-title">Decouvrir</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'about' }">Qui sommes-nous ?</router-link></li>
                            <li><router-link :to="{ name: 'conduct-charter' }">Charte de bonne conduite</router-link></li>
                            <li><router-link :to="{ name: 'map' }">Carte Interactive</router-link></li>
                        </ul>
                    </div>

                    <div class="footer-column">
                        <h4 class="footer-column-title">Plus</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'concept' }">Notre Concept</router-link></li>
                            <li><router-link :to="{ name: 'cookies' }">Utilisation des cookies</router-link></li>
                            <li><router-link :to="{ name: 'sitemap' }">Plan du site</router-link></li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="footer-bottom">
                <p class="footer-copyright">
                    &copy; {{ new Date().getFullYear() }} MyEcoCar. Tous les droits reserves.
                </p>
            </div>
        </footer>
    </div>
</template>

<style>
.app-container.contact,
.app-container.contact main,
.app-container.contact #main {
    min-height: 0 !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
}

.app-container.contact .view-container {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
}

.app-container.contact .footer-component {
    display: none !important;
}
</style>

<style scoped>
.contact-page {
    background: #f6f9f7;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.contact-hero {
    background: linear-gradient(135deg, #1f3f2e 0%, #2d5016 100%);
    color: #fff;
    padding: 80px 20px 60px;
    text-align: center;
}

.contact-hero-content {
    width: min(1400px, 96vw);
    margin: 0 auto;
}

.hero-inner {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    justify-content: center;
}

.contact-title {
    margin: 0;
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: -0.5px;
}

.contact-subtitle {
    margin: 0;
    font-size: 20px;
    color: rgba(255, 255, 255, 0.85);
    font-family: 'Heebo', 'Poppins', sans-serif;
}

.contact-container {
    width: min(860px, 96vw);
    margin: 0 auto;
    padding: 54px 16px 28px;
    flex: 1;
}

.contact-content {
    background: #fff;
    border: 1px solid #e3ebe5;
    border-radius: 10px;
    padding: 44px 52px;
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.04);
}

.contact-form {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.form-label {
    font-size: 15px;
    font-weight: 600;
    color: #1f3f2e;
    font-family: 'Poppins', sans-serif;
}

.form-input,
.form-textarea {
    width: 100%;
    padding: 13px 16px;
    border: 1.5px solid #d0ddd5;
    border-radius: 8px;
    font-size: 16px;
    font-family: 'Heebo', 'Poppins', sans-serif;
    color: #34463c;
    background: #f9fbfa;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
    outline: none;
}

.form-input:focus,
.form-textarea:focus {
    border-color: #116d34;
    border: 3px solid #116d34;
    background: #fff;
}

.form-input--error {
    border-color: #d9534f !important;
    box-shadow: 0 0 0 3px rgba(217, 83, 79, 0.1) !important;
}

.form-textarea {
    resize: vertical;
    min-height: 140px;
}

.form-error {
    font-size: 13px;
    color: #d9534f;
    font-family: 'Heebo', 'Poppins', sans-serif;
}

.form-error-general {
    padding: 14px 18px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 15px;
    font-family: 'Heebo', 'Poppins', sans-serif;
    text-align: center;
}

.recaptcha-notice {
    font-size: 12px;
    color: #6b7280;
    font-family: 'Heebo', 'Poppins', sans-serif;
    text-align: center;
    line-height: 1.5;
    padding: 8px 0;
}

.recaptcha-notice a {
    color: #2563eb;
    text-decoration: none;
}

.recaptcha-notice a:hover {
    text-decoration: underline;
}

.form-submit {
    align-self: flex-start;
    padding: 14px 36px;
    background: linear-gradient(135deg, #1f3f2e 0%, #2d5016 100%);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.form-submit:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-2px);
}

.form-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Footer */
.footer-section {
    background: linear-gradient(135deg, #1f3f2e 0%, #2d5016 100%);
    color: #ffffff;
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    margin-top: 1.5rem;
    margin-bottom: 0 !important;
    padding: 3rem 1rem 0;
}

.footer-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 0.8fr 1.2fr;
    gap: 3rem;
    align-items: start;
}

.footer-left {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.footer-logo-section {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.footer-logo {
    width: 50px;
    height: 50px;
    object-fit: contain;
}

.footer-brand-name {
    font-family: 'Poppins', sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: #aef7b9;
}

.footer-social-links {
    display: flex;
    gap: 1rem;
    align-items: center;
}

.social-link {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aef7b9;
    text-decoration: none;
    transition: all 0.3s ease;
}

.social-link:hover {
    background: rgba(174, 247, 185, 0.2);
    color: #ffffff;
    transform: translateY(-3px);
}

.social-link i {
    font-size: 1.25rem;
}

.footer-center {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
}

.footer-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.footer-column-title {
    font-family: 'Poppins', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: #aef7b9;
    margin: 0;
}

.footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.footer-links li {
    margin: 0;
}

.footer-links a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-family: 'Heebo', 'Poppins', sans-serif;
    font-size: 0.95rem;
    transition: color 0.3s ease;
}

.footer-links a:hover {
    color: #aef7b9;
    text-decoration: underline;
}

.footer-bottom {
    text-align: center;
    margin-top: 2rem;
    margin-bottom: 0;
    padding-top: 2rem;
    padding-bottom: 1.5rem;
    border-top: 1px solid rgba(174, 247, 185, 0.2);
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
}

.footer-copyright {
    font-family: 'Heebo', 'Poppins', sans-serif;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

@media (max-width: 1024px) {
    .footer-container {
        grid-template-columns: 1fr;
        gap: 2rem;
    }

    .footer-center {
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
    }
}

@media (max-width: 768px) {
    .contact-hero {
        padding: 50px 20px 40px;
    }

    .contact-title {
        font-size: 32px;
    }

    .contact-subtitle {
        font-size: 17px;
    }

    .contact-container {
        padding: 34px 12px 28px;
    }

    .contact-content {
        padding: 28px 24px;
    }

    .form-input,
    .form-textarea {
        font-size: 15px;
    }

    .form-submit {
        align-self: stretch;
        text-align: center;
    }

    .footer-section {
        padding: 2rem 1rem 100px;
        margin-top: 1rem;
    }

    .footer-left {
        gap: 1.5rem;
    }

    .footer-center {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }

    .footer-column {
        gap: 0.75rem;
    }

    .footer-column-title {
        font-size: 1rem;
    }

    .footer-links a {
        font-size: 0.9rem;
    }

    .footer-logo {
        width: 40px;
        height: 40px;
    }

    .footer-brand-name {
        font-size: 1.25rem;
    }
}

@media (max-width: 480px) {
    .contact-hero {
        padding: 40px 20px 30px;
    }

    .contact-title {
        font-size: 26px;
    }

    .contact-subtitle {
        font-size: 15px;
    }

    .contact-container {
        padding: 24px 12px;
    }

    .contact-content {
        padding: 20px 16px;
    }
}
</style>

<script>
import router from '../../router';
import bus from '../../services/bus-event.js';
import dialogs from '../../services/dialogs.js';
import recaptcha from '../../services/recaptcha.js';
import { ContactApi } from '../../services/api';

const contactApi = new ContactApi();

export default {
    name: 'contact',
    data() {
        return {
            form: {
                name: '',
                email: '',
                subject: '',
                message: ''
            },
            errors: {},
            isSubmitting: false,
            recaptchaConfigured: false
        };
    },
    computed: {
        showRecaptchaNotice() {
            const isLocalhost = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1' ||
                               window.location.hostname.includes('.local');
            return this.recaptchaConfigured && !isLocalhost;
        }
    },
    methods: {
        validate() {
            const errors = {};
            if (!this.form.name.trim()) {
                errors.name = 'Veuillez entrer votre nom complet.';
            }
            if (!this.form.email.trim()) {
                errors.email = 'Veuillez entrer votre adresse mail.';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                errors.email = 'Veuillez entrer une adresse mail valide.';
            }
            if (!this.form.subject.trim()) {
                errors.subject = 'Veuillez entrer un sujet.';
            }
            if (!this.form.message.trim()) {
                errors.message = 'Veuillez entrer votre message.';
            }
            return errors;
        },
        submitForm() {
            this.errors = this.validate();
            if (Object.keys(this.errors).length > 0) return;

            this.isSubmitting = true;

            // Check if we're on localhost or development environment
            const isLocalhost = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1' ||
                               window.location.hostname.includes('.local');

            // Get reCAPTCHA token before submitting (skip if localhost and not configured properly)
            const recaptchaPromise = (this.recaptchaConfigured && !isLocalhost) 
                ? recaptcha.execute('contact')
                : Promise.resolve('localhost-bypass');

            recaptchaPromise
                .then(token => {
                    const data = {
                        ...this.form,
                        recaptcha_token: token
                    };

                    return contactApi.submit(data);
                })
                .then((response) => {
                    this.isSubmitting = false;
                    const message = response.message || 'Votre message a bien été envoyé. Nous vous répondrons dans les plus brefs délais.';
                    dialogs.message(message, {
                        duration: 3,
                        position: 'top-right',
                        estado: 'success'
                    });
                    this.form = { name: '', email: '', subject: '', message: '' };
                    this.errors = {};
                })
                .catch((error) => {
                    this.isSubmitting = false;
                    if (error.data && error.data.errors) {
                        // Erreurs de validation du backend
                        const backendErrors = {};
                        Object.keys(error.data.errors).forEach(key => {
                            backendErrors[key] = error.data.errors[key][0];
                        });
                        this.errors = backendErrors;
                    } else {
                        this.errors = {
                            general: (error.data && error.data.message) || 'Une erreur est survenue lors de l\'envoi de votre message. Veuillez réessayer plus tard.'
                        };
                    }
                });
        },
        onBackClick() {
            router.back();
        }
    },
    mounted() {
        bus.on('back-click', this.onBackClick);
        
        // Check if reCAPTCHA is configured
        this.recaptchaConfigured = recaptcha.isConfigured();
        
        // Only load reCAPTCHA script if not on localhost
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.includes('.local');
        
        if (this.recaptchaConfigured && !isLocalhost) {
            recaptcha.loadScript().catch(err => {
                console.error('Failed to load reCAPTCHA:', err);
            });
        }
    },
    beforeDestroy() {
        bus.off('back-click', this.onBackClick);
    }
};
</script>
